import { Navigate, Route, Routes } from "react-router-dom";
import { PublicLayout, AdminLayout } from "./components/Layout";
import { PageError } from "./components/State";
import { RequireAuth, RequireRole } from "./components/Guards";
import { HomePage } from "./pages/Home";
import { PoliciesPage, PolicyDetailPage } from "./pages/Policies";
import {
  LegalDocumentPage,
  LoginPage,
  NotificationsPage,
  ProfilePage,
} from "./pages/Account";
import { ApplicationPage } from "./pages/Application";
import { VenturesPage } from "./pages/Ventures";
import { CommunityPage } from "./pages/Community";
import { DashboardPage } from "./pages/admin/Dashboard";
import {
  AdminApplicationDetailPage,
  AdminApplicationsPage,
  AdminMerchantsPage,
} from "./pages/admin/Applications";
import { AdminPoliciesPage, PolicyEditorPage } from "./pages/admin/Policies";
import { AdminUsersPage, AuditLogsPage } from "./pages/admin/Governance";
import { AdminForumPage } from "./pages/admin/Forum";
import { AdminExportsPage } from "./pages/admin/Exports";
import { AdminReviewTasksPage } from "./pages/admin/ReviewTasks";
import { AdminOperationsPage } from "./pages/admin/Operations";
import { AdminPortalSettingsPage } from "./pages/admin/PortalSettings";
import { AdminAnnouncementsPage } from "./pages/admin/Announcements";
import { AdminProgramsPage } from "./pages/admin/Programs";
import { AdminMCPKeysPage } from "./pages/admin/MCPKeys";
import {
  CreateProductAppPage,
  ProductDetailPage,
  ProductsPage,
} from "./pages/Products";
import {
  ProductAppDetailPage,
  ProductAppsPage,
} from "./pages/ProductApps";
import { WalletPage } from "./pages/Wallet";
import { InvitePage } from "./pages/Invite";
import { WorkspacesPage } from "./pages/Workspaces";
import {
  AdminAffRiskPage,
  AdminBillingPage,
  AdminProductRegistryPage,
  AdminProductRuntimePage,
} from "./pages/admin/ProductPlatform";

export function App() {
  return (
    <Routes>
      <Route errorElement={<PageError />}>
        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:productId" element={<ProductDetailPage />} />
          <Route path="policies" element={<PoliciesPage />} />
          <Route path="policies/:id" element={<PolicyDetailPage />} />
          <Route path="legal/:code" element={<LegalDocumentPage />} />
          <Route path="community" element={<CommunityPage />} />
          <Route element={<RequireAuth />}>
            <Route
              path="products/:productId/create"
              element={<CreateProductAppPage />}
            />
            <Route path="apps" element={<ProductAppsPage />} />
            <Route path="apps/:appId" element={<ProductAppDetailPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="invite" element={<InvitePage />} />
            <Route path="workspaces" element={<WorkspacesPage />} />
            <Route path="application" element={<ApplicationPage />} />
            <Route path="ventures" element={<VenturesPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Route>
        <Route path="login" element={<LoginPage />} />
        <Route
          element={
            <RequireRole
              roles={[
                "park_admin",
                "park_reviewer",
                "park_viewer",
                "super_admin",
                "policy_editor",
                "policy_approver",
                "community_moderator",
              ]}
            />
          }
        >
          <Route path="admin" element={<AdminLayout />}>
            <Route
              element={<RequireRole roles={["park_admin", "super_admin"]} />}
            >
              <Route index element={<DashboardPage />} />
              <Route path="products" element={<AdminProductRegistryPage />} />
              <Route
                path="product-apps"
                element={<AdminProductRuntimePage />}
              />
              <Route path="billing" element={<AdminBillingPage />} />
              <Route path="aff-risk" element={<AdminAffRiskPage />} />
              <Route path="portal" element={<AdminPortalSettingsPage />} />
              <Route path="programs" element={<AdminProgramsPage />} />
            </Route>
            <Route element={<RequireRole roles={["park_reviewer", "super_admin"]} />}>
              <Route path="mcp-keys" element={<AdminMCPKeysPage />} />
            </Route>
            <Route
              element={
                <RequireRole
                  roles={[
                    "park_admin",
                    "park_reviewer",
                    "park_viewer",
                    "super_admin",
                  ]}
                />
              }
            >
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route
                path="applications/:id"
                element={<AdminApplicationDetailPage />}
              />
              <Route path="review-tasks" element={<AdminReviewTasksPage />} />
              <Route path="merchants" element={<AdminMerchantsPage />} />
            </Route>
            <Route
              element={
                <RequireRole
                  roles={[
                    "park_admin",
                    "super_admin",
                    "policy_editor",
                    "policy_approver",
                  ]}
                />
              }
            >
              <Route path="policies" element={<AdminPoliciesPage />} />
              <Route path="policies/new" element={<PolicyEditorPage />} />
              <Route path="policies/:id/edit" element={<PolicyEditorPage />} />
            </Route>
            <Route
              element={
                <RequireRole
                  roles={["park_admin", "super_admin", "community_moderator"]}
                />
              }
            >
              <Route path="community" element={<AdminForumPage />} />
              <Route
                path="announcements"
                element={<AdminAnnouncementsPage />}
              />
            </Route>
            <Route element={<RequireRole roles={["super_admin"]} />}>
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="audit" element={<AuditLogsPage />} />
              <Route path="exports" element={<AdminExportsPage />} />
              <Route path="operations" element={<AdminOperationsPage />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
